import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import ScrollToTopButton from '../components/ScrollToTopButton';

// Styled Components
const PageHero = styled.section`
  height: 50vh;
  min-height: 300px;
  background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), 
    url('/images/reinigung_hero.jpg') no-repeat center center/cover;
  display: flex;
  align-items: center;
  text-align: center;
  color: white;
`;

const PageHeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  
  h1 {
    font-size: 3rem;
    margin-bottom: 20px;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    margin-bottom: 30px;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const Button = styled.button`
  display: inline-block;
  background-color: var(--accent-color);
  color: var(--dark-color);
  padding: 12px 30px;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: #e0a800;
    transform: translateY(-3px);
  }
`;

const Section = styled.section`
  padding: 60px 0;
  background-color: ${props => props.dark ? 'var(--light-color)' : 'white'};
  
  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 40px;
  
  h2 {
    font-size: 2.5rem;
    color: var(--primary-color);
    position: relative;
    display: inline-block;
    padding-bottom: 15px;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
    
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 0;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background-color: var(--accent-color);
    }
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ServiceCard = styled.div`
  background-color: var(--light-color);
  padding: 30px;
  border-radius: 10px;
  box-shadow: var(--shadow);
  transition: transform 0.3s, box-shadow 0.3s;
  height: 100%;
  cursor: pointer;
  position: relative;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
  
  &::after {
    content: 'Auswählen';
    position: absolute;
    bottom: 15px;
    right: 15px;
    background-color: var(--accent-color);
    color: var(--dark-color);
    padding: 5px 10px;
    border-radius: 5px;
    font-size: 0.8rem;
    font-weight: bold;
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover::after {
    opacity: 1;
  }
  
  h3 {
    color: var(--primary-color);
    margin-bottom: 15px;
    font-size: 1.5rem;
    position: relative;
    padding-bottom: 10px;
    
    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 50px;
      height: 3px;
      background-color: var(--accent-color);
    }
  }
  
  p {
    margin-bottom: 15px;
  }
  
  ul {
    padding-left: 20px;
    margin-bottom: 15px;
  }
`;

const TextCenter = styled.p`
  max-width: 800px;
  margin: 0 auto 40px;
  text-align: center;
  padding: 0 20px;
`;

const Process = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-top: 30px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ProcessStep = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 10px;
  box-shadow: var(--shadow);
  text-align: center;
  position: relative;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
  }
  
  &:last-child {
    grid-column: ${props => props.fullWidth ? '1 / 3' : 'auto'};
    
    @media (max-width: 768px) {
      grid-column: auto;
    }
  }
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  background-color: var(--primary-color);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin: 0 auto 15px;
`;

const AdvantageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 50px;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const AdvantageItem = styled.div`
  display: flex;
  align-items: flex-start;
  background-color: white;
  padding: 25px;
  border-radius: 10px;
  box-shadow: var(--shadow);
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const AdvantageIcon = styled.div`
  flex-shrink: 0;
  width: 50px;
  height: 50px;
  background-color: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  color: white;
  font-size: 1.5rem;
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 15px;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 20px;
  border-radius: 10px;
  margin: 0 15px 20px;
  text-align: center;
  box-shadow: var(--shadow);
  
  h3 {
    margin-bottom: 15px;
    color: #155724;
  }
  
  p {
    margin-bottom: 20px;
  }
`;
const AdvantageText = styled.div`
  h3 {
    color: var(--primary-color);
    margin-bottom: 10px;
  }
`;

const Faq = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FaqItem = styled.div`
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 5px;
  overflow: hidden;
`;

const FaqQuestion = styled.div`
  background-color: var(--light-color);
  padding: 15px 20px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
  
  &:after {
    content: '+';
    font-size: 1.2rem;
    transition: transform 0.3s;
    transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};
  }
`;

const FaqAnswer = styled.div`
  padding: 15px 20px;
  border-top: 1px solid #ddd;
  background-color: white;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const BookingSection = styled.section`
  padding: 80px 0;
  background-color: white;
  scroll-margin-top: 80px;
  
  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const BookingForm = styled.form`
  max-width: 900px;
  margin: 0 auto;
  padding: 30px;
  background-color: var(--light-color);
  border-radius: 10px;
  box-shadow: var(--shadow);
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
  
  @media (max-width: 768px) {
    margin: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  padding: 0 15px;
  width: ${props => props.half ? '50%' : '100%'};
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0;
  }
  
  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 16px;
    
    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
  
  textarea {
    height: 150px;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  
  input {
    width: auto;
    margin-right: 10px;
    margin-top: 4px;
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  background-color: var(--accent-color);
  color: var(--dark-color);
  padding: 12px 30px;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: #e0a800;
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ResponseMessage = styled.div`
  padding: 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  text-align: center;
  background-color: ${props => props.isError ? '#f8d7da' : '#d4edda'};
  color: ${props => props.isError ? '#721c24' : '#155724'};
`;

function Reinigung() {
  // State for FAQ accordions
  const [openFaqs, setOpenFaqs] = useState([]);
  
  // Create refs
  const bookingFormRef = useRef(null);
  const formRef = useRef(null);
  
  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    objectType: '',
    size: '',
    date: '',
    frequency: '',
    address: '',
    special_requirements: '',
    privacy: false
  });
  
  // State for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formResponse, setFormResponse] = useState({ message: '', isError: false, show: false });
  
  // Toggle FAQ open/closed state
  const toggleFaq = (index) => {
    if (openFaqs.includes(index)) {
      setOpenFaqs(openFaqs.filter(item => item !== index));
    } else {
      setOpenFaqs([...openFaqs, index]);
    }
  };
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle service card click
  const handleServiceCardClick = (serviceTitle) => {
    // Update the service selection in the form
    setFormData({
      ...formData,
      service: serviceTitle
    });
    
    // Scroll to the booking form with a small delay to ensure the form state is updated
    setTimeout(() => {
      document.getElementById('buchung').scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  
  // Handle hero button click (to scroll to booking form)
  const handleHeroButtonClick = () => {
    // Scroll to the booking form
    document.getElementById('buchung').scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Create a FormData object from the form
    const form = formRef.current;
    const formDataObj = new FormData(form);
    
    // Submit the form using fetch
    fetch('https://formsubmit.co/ajax/info@guardiansmoves.de', {
      method: 'POST',
      body: formDataObj,
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      setIsSubmitting(false);
      if (data.success) {
        setSubmitSuccess(true);
        // Reset form
        setFormData({
          name: '',
          phone: '',
          email: '',
          service: '',
          objectType: '',
          size: '',
          date: '',
          frequency: '',
          address: '',
          special_requirements: '',
          privacy: false
        });
      } else {
        setFormResponse({
          message: 'Beim Senden des Formulars ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.',
          isError: true,
          show: true
        });
      }
    })
    .catch(error => {
      setIsSubmitting(false);
      setFormResponse({
        message: 'Beim Senden des Formulars ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt per Telefon oder E-Mail.',
        isError: true,
        show: true
      });
      console.error('Error:', error);
    });
  };
  
  // FAQ data
  const faqData = [
    {
      question: 'Wie oft sollte eine professionelle Reinigung durchgeführt werden?',
      answer: 'Die optimale Häufigkeit hängt von verschiedenen Faktoren ab, wie z.B. der Nutzungsintensität, der Art der Räumlichkeiten und individuellen Hygieneanforderungen. Für Büroräume empfehlen wir in der Regel eine tägliche oder mindestens mehrmals wöchentliche Reinigung, während für weniger frequentierte Bereiche auch wöchentliche oder zweiwöchentliche Intervalle ausreichend sein können. Wir beraten Sie gerne individuell zu Ihren spezifischen Anforderungen.'
    },
    {
      question: 'Arbeiten Sie mit festen Ansprechpartnern?',
      answer: 'Ja, für jeden Kunden stellen wir einen festen Ansprechpartner zur Verfügung, der mit den spezifischen Anforderungen Ihrer Räumlichkeiten vertraut ist. Dies gewährleistet eine kontinuierliche und zuverlässige Betreuung sowie kurze Kommunikationswege bei Fragen oder Änderungswünschen.'
    },
    {
      question: 'Wie werden Ihre Mitarbeiter ausgewählt und geschult?',
      answer: 'Wir legen großen Wert auf qualifiziertes Personal. Alle unsere Mitarbeiter durchlaufen einen sorgfältigen Auswahlprozess und erhalten eine umfassende Einarbeitung sowie regelmäßige Schulungen zu Reinigungstechniken, Materialien und Sicherheitsstandards. Zudem achten wir auf Zuverlässigkeit, Sorgfalt und ein professionelles Auftreten unserer Teams.'
    },
    {
      question: 'Können Sie auch kurzfristige oder einmalige Reinigungen übernehmen?',
      answer: 'Ja, neben regelmäßigen Reinigungsaufträgen übernehmen wir auch gerne kurzfristige oder einmalige Einsätze, wie z.B. Grundreinigungen, Baureinigungen oder Reinigungen vor besonderen Anlässen. Kontaktieren Sie uns einfach mit Ihrem Anliegen, und wir bemühen uns, auch kurzfristige Termine zu realisieren.'
    },
    {
      question: 'Wie erfolgt die Abrechnung Ihrer Dienstleistungen?',
      answer: 'Die Abrechnung erfolgt in der Regel monatlich auf Basis der vereinbarten Leistungen. Wir bieten transparente Preismodelle ohne versteckte Kosten. Je nach Vereinbarung kann die Abrechnung nach Stunden, Fläche oder als Pauschalpreis erfolgen. Gerne erstellen wir Ihnen ein individuelles Angebot, das genau auf Ihre Bedürfnisse zugeschnitten ist.'
    }
  ];
  
  // Cleaning services data
  const cleaningServices = [
    {
      title: 'Unterhaltsreinigung',
      description: 'Regelmäßige Reinigung von Büro- und Geschäftsräumen. Wir sorgen für eine kontinuierliche Sauberkeit und ein angenehmes Arbeitsklima für Ihre Mitarbeiter und Kunden.',
      items: [
        'Reinigung von Böden aller Art',
        'Staubwischen und -saugen',
        'Sanitärreinigung',
        'Glasflächen- und Fensterreinigung',
        'Müllentsorgung'
      ]
    },
    {
      title: 'Grundreinigung',
      description: 'Eine intensive Reinigung, um hartnäckige Verschmutzungen zu beseitigen. Ideal für einen Neustart oder nach Renovierungsarbeiten.',
      items: [
        'Tiefenreinigung aller Oberflächen',
        'Entfernung von eingetretenen Verschmutzungen',
        'Grundreinigung von Sanitäranlagen',
        'Reinigung schwer zugänglicher Bereiche',
        'Entfernung von Kalkablagerungen'
      ]
    },
    {
      title: 'Fenster- und Glasreinigung',
      description: 'Streifenfreie Reinigung von Fenstern, Glastüren und Glasfassaden für maximale Transparenz und einen brillanten Eindruck.',
      items: [
        'Fensterflächen innen und außen',
        'Schaufenster und Vitrinen',
        'Glasfassaden und Glastüren',
        'Wintergärten',
        'Spiegelflächen'
      ]
    },
    {
      title: 'Treppenhausreinigung',
      description: 'Regelmäßige und gründliche Reinigung von Treppenhäusern in Wohn- und Geschäftsgebäuden für einen sauberen ersten Eindruck.',
      items: [
        'Reinigung von Treppenstufen und Podesten',
        'Reinigung von Geländern und Handläufen',
        'Reinigung von Eingangsbereichen',
        'Entfernung von Spinnweben',
        'Pflege von Briefkastenanlagen'
      ]
    },
    {
      title: 'Baureinigung',
      description: 'Nach Abschluss von Bauarbeiten oder Renovierungen sorgen wir für die Beseitigung von Baustaub und -schmutz, damit Ihre Räume schnell wieder nutzbar sind.',
      items: [
        'Entfernung von Bauschutt und -staub',
        'Reinigung aller Oberflächen',
        'Entfernung von Kleberesten und Farbflecken',
        'Fensterreinigung nach Bauarbeiten',
        'Feinreinigung für die Übergabe'
      ]
    },
    {
      title: 'Hausmeisterservice',
      description: 'Unser umfassender Hausmeisterservice kümmert sich um alle anfallenden Aufgaben in und um Ihre Immobilie.',
      items: [
        'Kleine Reparaturen und Instandhaltungsarbeiten',
        'Gartenpflege und Grünanlagenpflege',
        'Winterdienst',
        'Kontrolle technischer Anlagen',
        'Mülltonnen-Management'
      ]
    }
  ];
  
  // Process steps data
  const processSteps = [
    { 
      number: 1, 
      title: 'Bedarfsanalyse', 
      description: 'Wir analysieren Ihre spezifischen Anforderungen und besichtigen die zu reinigenden Flächen, um ein passendes Konzept zu erstellen.' 
    },
    { 
      number: 2, 
      title: 'Angebot', 
      description: 'Basierend auf der Bedarfsanalyse erstellen wir ein transparentes und detailliertes Angebot für unsere Dienstleistungen.' 
    },
    { 
      number: 3, 
      title: 'Planung', 
      description: 'Nach Auftragserteilung planen wir den optimalen Einsatz unserer Mitarbeiter und legen Reinigungsintervalle fest.' 
    },
    { 
      number: 4, 
      title: 'Durchführung', 
      description: 'Unsere geschulten Mitarbeiter führen die Reinigungsarbeiten gemäß den vereinbarten Leistungen und unter Einhaltung höchster Qualitätsstandards durch.' 
    },
    { 
      number: 5, 
      title: 'Qualitätskontrolle', 
      description: 'Regelmäßige Kontrollen und Feedback-Gespräche stellen sicher, dass unsere Leistungen Ihren Erwartungen entsprechen.', 
      fullWidth: true 
    }
  ];
  
  // Advantages data
  const advantages = [
    { 
      icon: 'M8 0a8 8 0 1 0 0 16A8 8 0 0 0 8 0zm3.5 7.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5z', 
      title: 'Qualifiziertes Personal', 
      description: 'Unsere Mitarbeiter sind geschult und erfahren in allen Bereichen der professionellen Reinigung.' 
    },
    { 
      icon: 'M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z', 
      title: 'Flexible Einsatzzeiten', 
      description: 'Wir passen uns Ihren Betriebszeiten an und arbeiten außerhalb Ihrer Geschäftszeiten, um Störungen zu minimieren.' 
    },
    { 
      icon: 'M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z', 
      title: 'Moderne Ausrüstung', 
      description: 'Wir setzen auf moderne, umweltfreundliche Reinigungsgeräte und -mittel für optimale Ergebnisse.' 
    },
    { 
      icon: 'M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z M9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588z', 
      title: 'Transparente Leistungen', 
      description: 'Klare Verträge und transparente Abrechnung ohne versteckte Kosten oder Zusatzgebühren.' 
    },
    { 
      icon: 'M2.5.5A.5.5 0 0 1 3 0h10a.5.5 0 0 1 .5.5c0 .538-.012 1.05-.034 1.536a3 3 0 1 1-1.133 5.89c-.79 1.865-1.878 2.777-2.833 3.011v2.173l1.425.356c.194.048.377.135.537.255L13.3 15.1a.5.5 0 0 1-.3.9H3a.5.5 0 0 1-.3-.9l1.838-1.379c.16-.12.343-.207.537-.255L6.5 13.11v-2.173c-.955-.234-2.043-1.146-2.833-3.012a3 3 0 1 1-1.132-5.89A33.076 33.076 0 0 1 2.5.5zm.099 2.54a2 2 0 0 0 .72 3.935c-.333-1.05-.588-2.346-.72-3.935zm10.083 3.935a2 2 0 0 0 .72-3.935c-.133 1.59-.388 2.885-.72 3.935z', 
      title: 'Qualitätskontrolle', 
      description: 'Regelmäßige Kontrollen und ein strukturiertes Feedback-System garantieren gleichbleibend hohe Qualität.' 
    },
    { 
      icon: 'M8 16a6 6 0 0 0 6-6c0-1.655-1.122-2.904-2.432-4.362C10.254 4.176 8.75 2.503 8 0c0 0-6 5.686-6 10a6 6 0 0 0 6 6Z M6.646 4.646l.708.708c-.29.29-.444.617-.512.985l-.707-.707c.11-.385.345-.719.511-.986Z', 
      title: 'Umweltfreundlichkeit', 
      description: 'Wir verwenden umweltschonende Reinigungsmittel und achten auf ressourcenschonende Arbeitsprozesse.' 
    }
  ];
  
  return (
    <>
      <PageHero>
        <div className="container">
          <PageHeroContent>
            <h1>Professionelle Reinigungsdienste</h1>
            <p>Sauberkeit und Hygiene für Ihren Wohn- und Arbeitsbereich durch unsere erfahrenen Teams</p>
            <Button onClick={handleHeroButtonClick}>Angebot anfordern</Button>
          </PageHeroContent>
        </div>
      </PageHero>

      <Section>
        <div className="container">
          <SectionTitle>
            <h2>Unsere Reinigungsleistungen</h2>
          </SectionTitle>
          
          <ServicesGrid>
            {cleaningServices.map((service, index) => (
              <ServiceCard 
                key={index}
                onClick={() => handleServiceCardClick(service.title)}
                className="service-card"
                role="button"
                tabIndex="0"
                aria-label={`Service auswählen: ${service.title}`}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleServiceCardClick(service.title);
                  }
                }}
              >
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <p>Unsere Leistungen umfassen:</p>
                <ul>
                  {service.items.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </div>
      </Section>

      <Section dark>
        <div className="container">
          <SectionTitle>
            <h2>So arbeiten wir</h2>
          </SectionTitle>
          <TextCenter>
            Unsere Arbeitsweise ist strukturiert und transparent. Wir legen Wert auf eine enge Zusammenarbeit mit unseren Kunden, um maßgeschneiderte Reinigungslösungen zu entwickeln und umzusetzen.
          </TextCenter>
          
          <Process>
            {processSteps.map((step, index) => (
              <ProcessStep 
                key={index}
                fullWidth={step.fullWidth}
              >
                <StepNumber>{step.number}</StepNumber>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </ProcessStep>
            ))}
          </Process>
        </div>
      </Section>

      <Section>
        <div className="container">
          <SectionTitle>
            <h2>Unsere Vorteile</h2>
          </SectionTitle>
          
          <AdvantageGrid>
            {advantages.map((advantage, index) => (
              <AdvantageItem key={index}>
                <AdvantageIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                    <path d={advantage.icon} />
                  </svg>
                </AdvantageIcon>
                <AdvantageText>
                  <h3>{advantage.title}</h3>
                  <p>{advantage.description}</p>
                </AdvantageText>
              </AdvantageItem>
            ))}
          </AdvantageGrid>
        </div>
      </Section>

      <Section dark>
        <div className="container">
          <SectionTitle>
            <h2>Häufig gestellte Fragen</h2>
          </SectionTitle>
          
          <Faq>
            {faqData.map((faq, index) => (
              <FaqItem key={index}>
                <FaqQuestion 
                  onClick={() => toggleFaq(index)}
                  isOpen={openFaqs.includes(index)}
                >
                  {faq.question}
                </FaqQuestion>
                <FaqAnswer isOpen={openFaqs.includes(index)}>
                  {faq.answer}
                </FaqAnswer>
              </FaqItem>
            ))}
          </Faq>
        </div>
      </Section>

      <BookingSection id="buchung" ref={bookingFormRef}>
        <div className="container">
          <SectionTitle>
            <h2>Online-Anfrage für Reinigungsservice</h2>
          </SectionTitle>
          
          {submitSuccess ? (
            <SuccessMessage>
              <h3>Vielen Dank für Ihre Buchungsanfrage!</h3>
              <p>Wir haben Ihre Anfrage erhalten und werden sie sorgfältig prüfen. Unser Team wird sich so schnell wie möglich mit Ihnen in Verbindung setzen, um die Details zu besprechen und Ihre Buchung zu bestätigen.</p>
              <Button as="button" onClick={() => setSubmitSuccess(false)} style={{ marginTop: '15px' }}>
                Neue Anfrage
              </Button>
            </SuccessMessage>
          ) : (
            <>
              {formResponse.show && (
                <ResponseMessage isError={formResponse.isError}>
                  {formResponse.message}
                </ResponseMessage>
              )}
              
              <BookingForm 
                onSubmit={handleSubmit}
                ref={formRef}
              >
                {/* FormSubmit Configuration - these are hidden fields */}
                <input type="hidden" name="_subject" value="Neue Reinigungsanfrage von der Website" />
                <input type="hidden" name="_captcha" value="false" />
                <input type="hidden" name="_template" value="table" />
                <input type="hidden" name="_honey" value="" /> {/* Spam honeypot */}
                
                <FormRow>
                  <FormGroup half>
                    <label htmlFor="name">Name *</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={formData.name}
                      onChange={handleChange}
                      required 
                    />
                  </FormGroup>
                  
                  <FormGroup half>
                    <label htmlFor="phone">Telefonnummer *</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      name="phone" 
                      value={formData.phone}
                      onChange={handleChange}
                      required 
                    />
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup half>
                    <label htmlFor="email">E-Mail *</label>
                    <input 
                      type="email" 
                      id="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleChange} 
                      required 
                    />
                  </FormGroup>
                  
                  <FormGroup half>
                    <label htmlFor="service">Gewünschter Service *</label>
                    <select 
                      id="service" 
                      name="service"
                      value={formData.service}
                      onChange={handleChange} 
                      required
                    >
                      <option value="">Bitte wählen</option>
                      {cleaningServices.map((service, index) => (
                        <option key={index} value={service.title}>
                          {service.title}
                        </option>
                      ))}
                      <option value="Sonstiges">Sonstiges</option>
                    </select>
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup half>
                    <label htmlFor="objectType">Art des Objekts *</label>
                    <select 
                      id="objectType" 
                      name="objectType"
                      value={formData.objectType}
                      onChange={handleChange} 
                      required
                    >
                      <option value="">Bitte wählen</option>
                      <option value="Bürogebäude">Bürogebäude</option>
                      <option value="Wohngebäude">Wohngebäude</option>
                      <option value="Privathaushalt">Privathaushalt</option>
                      <option value="Gewerbeimmobilie">Gewerbeimmobilie</option>
                      <option value="Arztpraxis">Arztpraxis</option>
                      <option value="Einzelhandel">Einzelhandel</option>
                      <option value="Gastronomie">Gastronomie</option>
                      <option value="Sonstiges">Sonstiges</option>
                    </select>
                  </FormGroup>
                  
                  <FormGroup half>
                    <label htmlFor="size">Ungefähre Fläche in m² *</label>
                    <input 
                      type="number" 
                      id="size" 
                      name="size" 
                      min="1"
                      value={formData.size}
                      onChange={handleChange} 
                      required 
                    />
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup half>
                    <label htmlFor="date">Gewünschtes Startdatum *</label>
                    <input 
                      type="date" 
                      id="date" 
                      name="date"
                      value={formData.date}
                      onChange={handleChange} 
                      required 
                    />
                  </FormGroup>
                  
                  <FormGroup half>
                    <label htmlFor="frequency">Gewünschte Häufigkeit *</label>
                    <select 
                      id="frequency" 
                      name="frequency"
                      value={formData.frequency}
                      onChange={handleChange} 
                      required
                    >
                      <option value="">Bitte wählen</option>
                      <option value="Einmalig">Einmalig</option>
                      <option value="Täglich">Täglich</option>
                      <option value="Mehrmals wöchentlich">Mehrmals wöchentlich</option>
                      <option value="Wöchentlich">Wöchentlich</option>
                      <option value="14-tägig">14-tägig</option>
                      <option value="Monatlich">Monatlich</option>
                      <option value="Nach Vereinbarung">Nach Vereinbarung</option>
                    </select>
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <label htmlFor="address">Adresse des zu reinigenden Objekts *</label>
                    <input 
                      type="text" 
                      id="address" 
                      name="address"
                      value={formData.address}
                      onChange={handleChange} 
                      required 
                    />
                  </FormGroup>
                </FormRow>
                
                <FormGroup>
                  <label htmlFor="special_requirements">Besondere Anforderungen oder Hinweise</label>
                  <textarea 
                    id="special_requirements" 
                    name="special_requirements"
                    value={formData.special_requirements}
                    onChange={handleChange}
                  ></textarea>
                </FormGroup>
                
                <FormGroup>
                  <CheckboxGroup>
                    <input 
                      type="checkbox" 
                      id="privacy" 
                      name="privacy"
                      checked={formData.privacy}
                      onChange={handleChange} 
                      required 
                    />
                    <label htmlFor="privacy">
                      Ich habe die <Link to="/datenschutz/" target="_blank">Datenschutzerklärung</Link> gelesen und akzeptiere diese. *
                    </label>
                  </CheckboxGroup>
                </FormGroup>
                
                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Wird gesendet...' : 'Anfrage senden'}
                </SubmitButton>
              </BookingForm>
            </>
          )}
        </div>
      </BookingSection>
      
      <ScrollToTopButton />
    </>
  );
}

export default Reinigung;